/**
 * Reset Password page — Phase 2L-A12.
 *
 * Public route. Reads `?token=…`, validates it on mount, then offers a
 * new-password + confirm-password form. On success shows a confirmation
 * state and auto-redirects to /login after 3s (no auto-login).
 *
 * Token states the backend can return on validate / reset:
 *   - invalid     — fake / malformed
 *   - expired     — past the 30-min TTL
 *   - used        — already consumed by an earlier reset
 *   - superseded  — a newer reset link was issued for the same user
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import PasswordRules from "@/components/PasswordRules";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TTL_TEXT = "30 minutes";
const REDIRECT_DELAY_MS = 3000;

const REASON_COPY = {
  invalid: {
    title: "This link isn't valid",
    body: "The reset link is malformed or never existed. Request a fresh one to continue.",
  },
  expired: {
    title: "This link has expired",
    body: `Reset links are only valid for ${TTL_TEXT}. Request a fresh one to continue.`,
  },
  used: {
    title: "This link has already been used",
    body: "Each reset link works once. Request a fresh one if you still need to change your password.",
  },
  superseded: {
    title: "A newer reset link was issued",
    body: "Only the most recent reset link works. Check your inbox for the latest email, or request a fresh one.",
  },
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [validationReason, setValidationReason] = useState(null); // null = valid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // ------ validate on mount ------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setValidationReason("invalid");
          setValidating(false);
        }
        return;
      }
      try {
        await api.get("/auth/reset-password/validate", { params: { token } });
        if (!cancelled) setValidationReason(null);
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (!cancelled) setValidationReason(typeof detail === "string" ? detail : "invalid");
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ------ auto-redirect on success ------
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => navigate("/login"), REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [success, navigate]);

  const passwordsMatch = useMemo(
    () => password.length > 0 && confirm.length > 0 && password === confirm,
    [password, confirm],
  );
  const lengthOk = password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!lengthOk) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }
    if (!passwordsMatch) {
      setErrorMsg("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password updated — please sign in.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 410 → token went bad mid-flow (e.g. another tab consumed it).
      if (e.response?.status === 410 && typeof detail === "string") {
        setValidationReason(detail);
      } else {
        setErrorMsg(formatApiError(detail) || "Could not reset password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ------ UI states ------
  let body;
  if (validating) {
    body = (
      <div
        data-testid="reset-password-validating"
        className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm flex items-center gap-3 text-slate-600"
      >
        <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
        <span>Checking your reset link…</span>
      </div>
    );
  } else if (validationReason) {
    const copy = REASON_COPY[validationReason] || REASON_COPY.invalid;
    body = (
      <div
        data-testid="reset-password-invalid-card"
        data-reason={validationReason}
        className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-3"
      >
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{copy.title}</h2>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{copy.body}</p>
          </div>
        </div>
        <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3">
          <Link
            to="/forgot-password"
            data-testid="reset-password-request-fresh"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-slate-900 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors"
          >
            Request a fresh link <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" className="text-sm font-semibold text-slate-500 hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  } else if (success) {
    body = (
      <div
        data-testid="reset-password-success-card"
        className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-3"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Password updated</h2>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
              Your password has been updated and any other sessions have been signed out.
              You&apos;ll be redirected to the sign-in page in a moment.
            </p>
          </div>
        </div>
        <div className="pt-3 border-t border-slate-100">
          <Link
            to="/login"
            data-testid="reset-password-go-to-login"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 hover:underline"
          >
            Go to sign in now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  } else {
    body = (
      <form
        onSubmit={submit}
        data-testid="reset-password-form"
        className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-4"
      >
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">New password</Label>
          <Input
            data-testid="reset-password-new"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-1.5 h-12 rounded-xl"
            autoComplete="new-password"
          />
          <PasswordRules password={password} testIdPrefix="reset-password" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Confirm new password</Label>
          <Input
            data-testid="reset-password-confirm"
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter the password"
            className="mt-1.5 h-12 rounded-xl"
            autoComplete="new-password"
          />
          {confirm.length > 0 && !passwordsMatch && (
            <p data-testid="reset-password-mismatch" className="mt-1.5 text-xs text-rose-600">
              Passwords don&apos;t match yet.
            </p>
          )}
        </div>
        {errorMsg && (
          <div
            data-testid="reset-password-error"
            className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
          >
            {errorMsg}
          </div>
        )}
        <Button
          data-testid="reset-password-submit"
          disabled={submitting || !lengthOk || !passwordsMatch}
          type="submit"
          size="lg"
          className="w-full btn-aqua rounded-full font-semibold mt-2"
        >
          {submitting
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Updating…</>
            : <>Set new password <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
        <p className="text-center text-xs text-slate-500 pt-2">
          <Link to="/login" className="font-semibold text-slate-600 hover:underline">← Back to sign in</Link>
        </p>
      </form>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Account recovery</div>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">Reset your password</h1>
            <p className="mt-2 text-slate-600">Choose a new password to sign in with.</p>
          </div>
          {body}
        </div>
      </main>
    </div>
  );
}
